import { defineComponent as _defineComponent } from 'vue'
import EditLotModal from "@/components/ui/modals/EditLotModal.vue";
  import LotDetailsConfirmModal from "@/components/ui/modals/LotDetailsConfirmModal.vue";
  import ReleaseLotsModal from "@/components/ui/modals/ReleaseLotsModal.vue";
  import { LotStatus } from "@/enums/LotStatus";
  import {
    onMounted,
    onUpdated,
    ref,
    watch,
    computed,
  } from "vue";
  import { useStore } from "vuex";
  import dayjs from "dayjs";
  import { formatDate } from "@/helpers/date";
  import { DocumentTypeEnum } from "@/enums/DocumentTypeEnum";
  import { getFirstLastName } from "@/helpers/user";
  import { useLotHook } from "@/hooks/lot";
  import router from "@/router";
  import { LotActionInterface } from "@/types/LotActionInterface";
  import { LotInterface } from "@/types/LotInterface";
  import {
    formatPrice,
    formatStripePrice,
  } from "@/helpers/transformation/price";
  import { useToast } from "vue-toastification";
  import { validDocumentsTypes } from "@/helpers/fileUpload";
  import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
  import { EoiStatus } from "@/enums/EoiStatus";
  import { copyToClipboard } from "@/helpers/copyToClipboard";
  import { isLotOwner } from "@/helpers/lot/isLotOwner";
  import LotConditionsEnum from "@/v2/enums/LotConditions";
  import { Business } from "@/types/BusinessInterface";
  import { AxiosResponse } from "axios";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotActions',
  props: {
    eois: {
      type: Object,
      required: true,
    },
    lot: {
      type: Object,
      required: true,
    },
  },
  emits: [
    "updateLot",
    "changeTab",
    "loading",
    "releaseLot",
  ],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  dayjs.extend(isSameOrBefore);
  const { showReleaseModal } = useLotHook();
  const store = useStore();
  const toast = useToast();
  const emits = __emit;
  const lotId = router.currentRoute.value.params.lotId;
  const props = __props;
  const upcomingActionsRef = ref<HTMLElement | null>(null);
  const hasUpcomingActions = ref(false);
  const checkUpcomingActions = () => upcomingActionsRef.value?.children.length;
  const showChangeDateModal = ref(false);
  const isSkipUploadContract = ref(false);
  const showConfirmActionModal = ref("");
  const pastActions = ref<LotActionInterface[] | null>(null);
  const documentTypes = validDocumentsTypes.join(", ");

  const estateId = computed(() => {
    return store.state.user.selectedEstateId;
  });

  const getPreContractOrHoldStatus = computed(() => {
    return props.lot.status === LotStatus.OFFLINE
      ? LotStatus.HOLD
      : LotStatus.PRE_CONTRACT;
  });
  const userBusiness = ref<Business | null>(null);
  const estate = computed(() =>
    store.state.user.landownerEstates.find((e) => e._id === estateId.value)
  );
  const canManageLot = computed(() =>
    isLotOwner(props.lot as LotInterface, userBusiness.value, estate.value)
  );

  const getUserBusiness = () => {
    store
      .dispatch("business/getBusinessByLandownerId")
      .then((res: AxiosResponse<Business>) => {
        userBusiness.value = res.data;
      });
  };

  onMounted(() => {
    getPastActions();
    getUserBusiness();
  });
  onUpdated(() => {
    hasUpcomingActions.value = !!checkUpcomingActions();
  });
  watch(
    () => props.lot,
    () => {
      getPastActions();
    }
  );

  function getPastActions() {
    store.dispatch("lot/getLotActions", lotId).then((res) => {
      pastActions.value = res.data;
    });
  }
  function updateLot() {
    getPastActions();
    emits("updateLot");
  }
  function changeLotStatus(status: LotStatus) {
    const data: { status: LotStatus; conditions?: string } = { status: status };

    if (status === LotStatus.UNCONDITIONAL) {
      data.conditions = LotConditionsEnum.UNCONDITIONAL;
    }

    store
      .dispatch("lot/changeLotStatus", {
        id: props.lot._id,
        data: data,
      })
      .then(() => {
        updateLot();
      });
  }
  function changeTab(tab: string) {
    emits("changeTab", tab);
  }
  const uploadContract = (e: any) => {
    if (!e.target.files.length) {
      return;
    }

    const data = new FormData();

    data.append("file", e.target.files[0]);
    data.append("documentType", DocumentTypeEnum.CONTRACTS);
    data.append("lotId", props.lot._id);
    data.append("estateId", estateId.value);
    store.dispatch("lot/uploadLotDocuments", data).then(() => {
      updateLot();
    });
  };

  function canChangeLotDate(date: string) {
    return dayjs(date).isSameOrBefore(dayjs(), "day");
  }
  function generateContract() {
    emits("loading");
    store
      .dispatch("eoi/downloadPdf", props.lot.eoiId?._id)
      .then((res) => {
        updateLot();
        window.open(res.data, "_blank");
      })
      .catch(() => toast.error("Something went wrong"));
  }
  function filterPastActions(fields: Array<keyof LotInterface>) {
    const allowedFields: Array<keyof LotInterface> = [
      "title",
      "blockWidth",
      "blockDepth",
      "size",
      "price",
      "status",
      "registrationDate",
      "financeDueDate",
      "settlementDate",
      "buyerId",
      "deposit",
      "offlineSale",
    ];

    return fields.filter((value) => allowedFields.includes(value));
  }
  const normalizedChangedValue = (
    field: keyof LotInterface,
    value: string | number
  ) => {
    if (field === "buyerId") {
      return value ? "Approved" : "Revoked";
    }

    if (field.includes("Date")) {
      return formatDate(value);
    }

    if (field === "price") {
      return formatPrice(value);
    }

    return value;
  };
  const formatField = (field: string, action: LotActionInterface) => {
    if (field === "buyerId") {
      const name = action?.buyerDetails
        ? ` from ${action.buyerDetails?.firstName} ${action.buyerDetails?.lastName}`
        : "";

      return "Contract Information Form" + name;
    }

    if (field === "offlineSale") {
      if (!action.newValues?.offlineSale) {
        return "Offline mode";
      }

      if (action.newValues?.offlineSale?.salePhase) {
        return "Sale Phase";
      }

      if (typeof action.newValues?.offlineSale?.offlineDeposit === "number") {
        return "Offline deposit";
      }
    }

    const result = field.replace(/([A-Z])/g, " $1");

    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  function closeReleaseModal(confirm: boolean) {
    if (confirm) {
      emits("releaseLot");
      updateLot();
    }

    showReleaseModal.value = false;
  }
  const getPaymentUrl = () => {
    store.dispatch("eoi/getPaymentUrl", props.lot.eoiId._id).then((res) => {
      copyToClipboard(res.data, "New payment link was copied to clipboard");
    });
  };

const __returned__ = { showReleaseModal, store, toast, emits, lotId, props, upcomingActionsRef, hasUpcomingActions, checkUpcomingActions, showChangeDateModal, isSkipUploadContract, showConfirmActionModal, pastActions, documentTypes, estateId, getPreContractOrHoldStatus, userBusiness, estate, canManageLot, getUserBusiness, getPastActions, updateLot, changeLotStatus, changeTab, uploadContract, canChangeLotDate, generateContract, filterPastActions, normalizedChangedValue, formatField, closeReleaseModal, getPaymentUrl, EditLotModal, LotDetailsConfirmModal, ReleaseLotsModal, get LotStatus() { return LotStatus }, onMounted, onUpdated, ref, watch, computed, get useStore() { return useStore }, get dayjs() { return dayjs }, get formatDate() { return formatDate }, get DocumentTypeEnum() { return DocumentTypeEnum }, get getFirstLastName() { return getFirstLastName }, get useLotHook() { return useLotHook }, get router() { return router }, get LotActionInterface() { return LotActionInterface }, get LotInterface() { return LotInterface }, get formatPrice() { return formatPrice }, get formatStripePrice() { return formatStripePrice }, get useToast() { return useToast }, get validDocumentsTypes() { return validDocumentsTypes }, get isSameOrBefore() { return isSameOrBefore }, get EoiStatus() { return EoiStatus }, get copyToClipboard() { return copyToClipboard }, get isLotOwner() { return isLotOwner }, get LotConditionsEnum() { return LotConditionsEnum }, get Business() { return Business }, get AxiosResponse() { return AxiosResponse } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})